import { hexToLinear, hexToRgb } from "./color.ts";
import { DEFAULT_CAMERA, evaluate, type CameraState, type CaptionState, type ObjectState, type SceneState } from "./evaluate.ts";
import { ease } from "./easing.ts";
import { bloomLevels, lookIsNeutral, pieceProgress, revealFx, TRANSITION_INDEX } from "./fx.ts";
import { meshGeometry, quad, type Geometry } from "./geometry.ts";
import { cameraMatrices, modelMatrix, multiply, rad, rotationX, scaling, transformPoint, translation, type Mat4, type Vec3 } from "./math.ts";
import { BACKDROP_FS, BLUR_FS, BRIGHT_FS, COPY_FS, FULLSCREEN_VS, PARTICLE_FS, PARTICLE_VS, POST_FS, TRANSITION_FS } from "./shaders.ts";
import { blankHtmlCard, cardKey, hasShadow, htmlKey, rasterCaption, rasterHtml, rasterImage, rasterPath, rasterShape, rasterText, type Card, type Layer, type Unit } from "./textures.ts";
import type { BackdropKind, HtmlElement, ImageElement, Look, MeshElement, ParticlesElement, Project, ShapeElement, TextElement, VideoElement } from "./types.ts";

const MESH_VS = `#version 300 es
layout(location=0) in vec3 aPos; layout(location=1) in vec3 aNormal;
uniform mat4 uModel; uniform mat4 uViewProj;
out vec3 vWorld; out vec3 vN;
void main(){
  vec4 w = uModel * vec4(aPos, 1.0);
  vWorld = w.xyz;
  vN = transpose(inverse(mat3(uModel))) * aNormal;
  gl_Position = uViewProj * w;
}`;
const MESH_FS = `#version 300 es
precision highp float; precision highp int;
in vec3 vWorld; in vec3 vN; out vec4 outColor;
uniform vec3 uAlbedo; uniform float uMetal; uniform float uRough; uniform vec3 uEmissive; uniform float uOpacity;
uniform vec3 uEye; uniform vec3 uLightDir; uniform vec3 uKey; uniform float uAmbient; uniform vec3 uSky; uniform vec3 uGround;
vec3 hemi(vec3 n){ return mix(uGround, uSky, n.y * 0.5 + 0.5); }
void main(){
  vec3 N = normalize(vN);
  vec3 V = normalize(uEye - vWorld);
  if (dot(N, V) < 0.0) N = -N;                       // double-sided
  vec3 L = normalize(uLightDir);
  vec3 H = normalize(L + V);
  float NdotL = max(dot(N, L), 0.0), NdotV = max(dot(N, V), 0.0);
  vec3 F0 = mix(vec3(0.04), uAlbedo, uMetal);
  vec3 F = F0 + (1.0 - F0) * pow(1.0 - NdotV, 5.0);
  float shin = mix(300.0, 6.0, uRough);
  float norm = (shin + 8.0) / 25.1327;
  vec3 spec = F * pow(max(dot(N, H), 0.0), shin) * norm * uKey * NdotL;
  vec3 diffuse = uAlbedo * (1.0 - uMetal) * (uKey * NdotL + hemi(N) * uAmbient);
  vec3 R = reflect(-V, N);
  vec3 env = hemi(R) * F * uAmbient * (1.0 - uRough * 0.75);
  vec3 c = diffuse + spec + env + uEmissive;
  c = c / (1.0 + max(max(c.r, c.g), c.b) * 0.15);    // gentle highlight roll-off
  c = pow(max(c, 0.0), vec3(1.0 / 2.2));
  outColor = vec4(c * uOpacity, uOpacity);          // premultiplied
}`;
const CARD_VS = `#version 300 es
layout(location=0) in vec3 aPos; layout(location=2) in vec2 aUV;
uniform mat4 uModel; uniform mat4 uViewProj; uniform vec4 uRect; out vec2 vUV; out vec2 vLocal;
void main(){ vLocal = aUV; vUV = uRect.xy + aUV * uRect.zw; gl_Position = uViewProj * uModel * vec4(aPos, 1.0); }`;
const CARD_FS = `#version 300 es
precision highp float; precision highp int;
in vec2 vUV; in vec2 vLocal; out vec4 outColor; uniform sampler2D uTex; uniform float uOpacity; uniform float uLod; uniform float uWipe;
void main(){
  vec4 c = uLod > 0.01 ? textureLod(uTex, vUV, uLod) : texture(uTex, vUV);
  float k = 1.0;
  if (uWipe >= 0.0 && uWipe < 1.0) k = 1.0 - smoothstep(uWipe - 0.06, uWipe, vLocal.x * 1.06 - 0.03);
  outColor = c * (uOpacity * k);
}`;
const BG_VS = `#version 300 es
out float vY;
void main(){ vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2); vY = 1.0 - p.y; gl_Position = vec4(p * 2.0 - 1.0, 0.999, 1.0); }`;
const BG_FS = `#version 300 es
precision highp float; precision highp int; in float vY; out vec4 outColor; uniform vec3 uTop; uniform vec3 uBottom;
void main(){ outColor = vec4(mix(uTop, uBottom, vY), 1.0); }`;

interface GpuMesh { vao: WebGLVertexArrayObject; count: number }
interface GpuCard { tex: WebGLTexture; width: number; height: number; units?: { chars: Unit[]; words: Unit[]; lines: Unit[] } }
type Draw = { depth: number; layer?: number; run: () => void };

/** An offscreen colour target; optionally multisampled (resolved into `tex` by resolve()) and/or with a depth buffer. */
interface Target { w: number; h: number; tex: WebGLTexture; fbo: WebGLFramebuffer; draw: WebGLFramebuffer; ms: boolean; garbage: (() => void) }

const BACKDROP_INDEX: Record<BackdropKind, number> = { none: 0, aurora: 1, radial: 2, grid: 3, dots: 4, noise: 5 };
const PARTICLE_INDEX = { dots: 0, sparks: 1, bokeh: 2, snow: 3, embers: 4, stars: 5 } as const;
const MAX_PARTICLES = 3000;

function compile(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const mk = (type: number, src: string) => {
    const s = gl.createShader(type)!; gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error("shader: " + gl.getShaderInfoLog(s));
    return s;
  };
  const p = gl.createProgram()!;
  gl.attachShader(p, mk(gl.VERTEX_SHADER, vs)); gl.attachShader(p, mk(gl.FRAGMENT_SHADER, fs)); gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error("link: " + gl.getProgramInfoLog(p));
  return p;
}

const SOURCES: Record<string, [string, string]> = {
  mesh: [MESH_VS, MESH_FS], card: [CARD_VS, CARD_FS], bg: [BG_VS, BG_FS], backdrop: [FULLSCREEN_VS, BACKDROP_FS],
  particles: [PARTICLE_VS, PARTICLE_FS], transition: [FULLSCREEN_VS, TRANSITION_FS], copy: [FULLSCREEN_VS, COPY_FS],
  bright: [FULLSCREEN_VS, BRIGHT_FS], blur: [FULLSCREEN_VS, BLUR_FS], post: [FULLSCREEN_VS, POST_FS],
};

export class Renderer {
  readonly gl: WebGL2RenderingContext;
  private progs = new Map<string, WebGLProgram>();
  private meshes = new Map<string, GpuMesh>(); private cards = new Map<string, GpuCard>();
  private quadMesh: GpuMesh; private emptyVao: WebGLVertexArrayObject; private aniso: EXT_texture_filter_anisotropic | null;
  private u = new Map<string, WebGLUniformLocation | null>();
  private targets = new Map<string, Target>();
  private samples: number;

  readonly canvas: HTMLCanvasElement;

  /** Logical (project) size: positions and cameras are expressed in it, whatever the output resolution is. */
  readonly lw: number; readonly lh: number;
  private playing = false;

  /** `outWidth`/`outHeight` render the same picture on a smaller (or larger) canvas: used for quick exports. */
  constructor(canvas: HTMLCanvasElement, width: number, height: number, outWidth: number = width, outHeight: number = height) {
    this.canvas = canvas; this.lw = width; this.lh = height;
    canvas.width = outWidth; canvas.height = outHeight;
    const gl = canvas.getContext("webgl2", { antialias: true, alpha: false, preserveDrawingBuffer: true, premultipliedAlpha: false });
    if (!gl) throw new Error("WebGL2 is not available");
    this.gl = gl;
    this.samples = Math.min(4, (gl.getParameter(gl.MAX_SAMPLES) as number) || 0);
    this.quadMesh = this.upload(quad());
    this.emptyVao = gl.createVertexArray()!;
    this.aniso = gl.getExtension("EXT_texture_filter_anisotropic");
  }

  /** Gives the GPU context back (many small previews would otherwise exhaust the browser's limit). */
  dispose(): void { try { this.gl.getExtension("WEBGL_lose_context")?.loseContext(); } catch { /* already gone */ } }

  setOutputSize(width: number, height: number): void {
    const w = Math.max(2, Math.round(width)), h = Math.max(2, Math.round(height));
    if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
  }

  setPlayback(playing: boolean, project: Project, timeMs: number): void {
    if (this.playing === playing) return;
    this.playing = playing;
    if (playing) void this.seekVideos(project, timeMs).then(() => {
      for (const entry of this.videos.values()) if (entry.ok) void entry.video.play().catch(() => undefined);
    });
    else for (const entry of this.videos.values()) entry.video.pause();
  }

  /** Programs compile on first use, so a page that never uses (say) transitions never pays for them. */
  private prog(name: string): WebGLProgram {
    let p = this.progs.get(name);
    if (!p) { const [vs, fs] = SOURCES[name]!; p = compile(this.gl, vs, fs); this.progs.set(name, p); }
    return p;
  }

  private loc(p: WebGLProgram, name: string, tag: string): WebGLUniformLocation | null {
    const k = tag + ":" + name;
    if (!this.u.has(k)) this.u.set(k, this.gl.getUniformLocation(p, name));
    return this.u.get(k)!;
  }

  private upload(g: Geometry): GpuMesh {
    const gl = this.gl, vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const attr = (loc: number, data: Float32Array, size: number) => {
      const b = gl.createBuffer()!; gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
    };
    attr(0, g.positions, 3); attr(1, g.normals, 3); attr(2, g.uvs, 2);
    const ib = gl.createBuffer()!; gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, g.indices, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    return { vao, count: g.indices.length };
  }

  private meshFor(el: MeshElement): GpuMesh {
    const key = `${el.mesh}:${el.size}`;
    let m = this.meshes.get(key);
    if (!m) { m = this.upload(meshGeometry(el.mesh, el.size)); this.meshes.set(key, m); }
    return m;
  }

  private upload2d(raster: Card): GpuCard {
    const gl = this.gl, tex = gl.createTexture()!;
    const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null;     // uploading must not disturb what a draw has bound
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, raster.canvas as HTMLCanvasElement);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (this.aniso) gl.texParameterf(gl.TEXTURE_2D, this.aniso.TEXTURE_MAX_ANISOTROPY_EXT, 8);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
    return { tex, width: raster.width, height: raster.height, units: raster.units };
  }

  private cacheCard(key: string, make: () => Card): GpuCard {
    let c = this.cards.get(key);
    if (c) { this.cards.delete(key); this.cards.set(key, c); return c; }     // refresh LRU order
    // Bounded cache: an interactive editor creates a new texture on every edit of text/size/colour.
    while (this.cards.size >= 256) {
      const oldest = this.cards.keys().next().value as string;
      this.gl.deleteTexture(this.cards.get(oldest)!.tex); this.cards.delete(oldest);
    }
    c = this.upload2d(make());
    this.cards.set(key, c);
    return c;
  }

  /** Put an already rasterised card in the bounded cache (same eviction as cacheCard). */
  private putCard(key: string, raster: Card): GpuCard {
    while (this.cards.size >= 256) {
      const oldest = this.cards.keys().next().value as string;
      this.gl.deleteTexture(this.cards.get(oldest)!.tex); this.cards.delete(oldest);
    }
    const c = this.upload2d(raster); this.cards.set(key, c); return c;
  }

  // ---- html elements: rasterised asynchronously (the browser decodes the SVG image off-thread) --------------------------
  private htmlPending = new Map<string, Promise<void>>();
  private htmlLast = new Map<string, GpuCard>();         // last picture drawn for each element: shown while the next one decodes
  private loadHtml(el: HtmlElement, clockMs: number): Promise<void> {
    const key = htmlKey(el, clockMs);
    if (this.cards.has(key)) return Promise.resolve();
    let p = this.htmlPending.get(key);
    if (!p) {
      p = rasterHtml(el, clockMs).then((raster) => { this.putCard(key, raster); }).finally(() => { this.htmlPending.delete(key); this.onAssetReady?.(); });
      this.htmlPending.set(key, p);
    }
    return p;
  }
  private htmlCard(el: HtmlElement, clockMs: number): GpuCard {
    const key = htmlKey(el, clockMs), hit = this.cards.get(key);
    if (hit) { this.cards.delete(key); this.cards.set(key, hit); this.htmlLast.set(el.id, hit); return hit; }
    void this.loadHtml(el, clockMs);
    const last = this.htmlLast.get(el.id);
    if (last) return last;
    return this.cacheCard(`h|blank|${el.id}|${el.width}|${el.height}`, () => blankHtmlCard(el));
  }
  /** Make sure every html element visible at `timeMs` has its exact picture (export path: nothing is ever drawn stale). */
  private async prepareHtml(project: Project, timeMs: number): Promise<void> {
    const frame = evaluate(project, timeMs), waits: Promise<void>[] = [];
    for (const sc of frame.scenes) for (const o of sc.objects) if (o.element.type === "html") waits.push(this.loadHtml(o.element, o.mediaTimeMs ?? 0));
    await Promise.all(waits);
  }

  private cardFor(el: TextElement | ShapeElement | ImageElement | VideoElement | HtmlElement, layer: Layer, draw = 1, morphU = 0, clockMs = 0): GpuCard {
    if (el.type === "html") return this.htmlCard(el, clockMs);
    if (el.type === "image" || el.type === "video") {
      if (el.type === "video") {
        const entry = this.videoFor(el.id, el.asset_id);
        const key = `v|${el.id}|${layer}`;
        const c = this.cards.get(key);
        if (c && entry?.ok && layer === "glyph") {
          const raster = rasterImage(el, entry.video, layer);
          const gl = this.gl; gl.bindTexture(gl.TEXTURE_2D, c.tex);
          gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, raster.canvas as HTMLCanvasElement);
          gl.generateMipmap(gl.TEXTURE_2D);
          return c;
        }
        return this.cacheCard(key, () => rasterImage(el, entry?.ok ? entry.video : null, layer));
      }
      const img = this.imageFor(el.asset_id);
      return this.cacheCard(cardKey(el, layer, img !== null), () => rasterImage(el, img, layer));
    }
    if (el.type === "shape" && el.shape === "path") {
      const d = Math.round(Math.min(Math.max(draw, 0), 1) * 240) / 240, m = Math.round(Math.min(Math.max(morphU, 0), 1) * 240) / 240;
      return this.cacheCard(cardKey(el, layer, true, d, m), () => rasterPath(el, layer, d, m));
    }
    return this.cacheCard(cardKey(el, layer), () => (el.type === "text" ? rasterText(el, layer) : rasterShape(el, layer)));
  }

  // ---- pictures: loaded lazily from a resolver the host provides (a URL, or a data: URL when exporting) ----------------
  private assetUrl: ((assetId: string) => string) | null = null;
  private pics = new Map<string, { img: HTMLImageElement; ok: boolean; done: Promise<void> }>();
  private videos = new Map<string, { video: HTMLVideoElement; ok: boolean; done: Promise<void>; assetId: string }>();
  /** Called whenever a picture finishes loading, so the host can draw the frame again. */
  onAssetReady: (() => void) | null = null;
  setAssetResolver(fn: ((assetId: string) => string) | null): void { this.assetUrl = fn; }
  private picFor(id: string): { img: HTMLImageElement; ok: boolean; done: Promise<void> } | null {
    let e = this.pics.get(id);
    if (!e && this.assetUrl) {
      const img = new Image(); img.decoding = "async";
      const entry = { img, ok: false, done: Promise.resolve() };
      entry.done = new Promise<void>((res) => {
        img.onload = () => { entry.ok = true; res(); this.onAssetReady?.(); };
        img.onerror = () => { res(); this.onAssetReady?.(); };
      });
      img.src = this.assetUrl(id);
      this.pics.set(id, entry); e = entry;
    }
    return e ?? null;
  }
  private imageFor(id: string): HTMLImageElement | null { const e = this.picFor(id); return e && e.ok ? e.img : null; }
  private videoFor(id: string, assetId: string): { video: HTMLVideoElement; ok: boolean; done: Promise<void>; assetId: string } | null {
    let e = this.videos.get(id);
    if (e && e.assetId !== assetId) { e.video.pause(); e.video.src = ""; this.videos.delete(id); e = undefined; }
    if (!e && this.assetUrl) {
      const video = document.createElement("video"); video.crossOrigin = "anonymous"; video.preload = "auto"; video.muted = true; video.playsInline = true;
      const entry = { video, ok: false, done: Promise.resolve(), assetId };
      entry.done = new Promise<void>((resolve) => {
        const ready = () => { entry.ok = true; resolve(); this.onAssetReady?.(); };
        video.addEventListener("loadeddata", ready, { once: true });
        video.addEventListener("error", () => { resolve(); this.onAssetReady?.(); }, { once: true });
      });
      video.src = this.assetUrl(assetId); this.videos.set(id, entry); e = entry;
    }
    return e ?? null;
  }
  private async seekVideos(project: Project, timeMs: number): Promise<void> {
    const frame = evaluate(project, timeMs);
    const seeks: Promise<void>[] = [];
    for (const scene of frame.scenes) for (const o of scene.objects) if (o.element.type === "video") {
      const ent = this.videoFor(o.id, o.element.asset_id); if (!ent) continue;
      await ent.done;
      const target = Math.max(0, (o.mediaTimeMs ?? 0) / 1000);
      if (Math.abs(ent.video.currentTime - target) > 0.02) seeks.push(new Promise<void>((resolve) => {
        const done = () => { ent.video.removeEventListener("seeked", done); resolve(); };
        ent.video.addEventListener("seeked", done, { once: true }); ent.video.currentTime = target;
      }));
    }
    await Promise.all(seeks);
  }
  async renderFrameAsync(project: Project, timeMs: number): Promise<void> { await Promise.all([this.seekVideos(project, timeMs), this.prepareHtml(project, timeMs)]); this.renderFrame(project, timeMs); }
  /** Resolves when every picture the project uses has loaded (or failed). The export waits for this before the first frame. */
  async assetsReady(project: Project): Promise<void> {
    const waits: Promise<void>[] = [];
    for (const el of Object.values(project.elements)) {
      if (el.type === "image") { const e = this.picFor(el.asset_id); if (e) waits.push(e.done); }
      if (el.type === "video") { const e = this.videoFor(el.id, el.asset_id); if (e) waits.push(e.done); }
    }
    await Promise.all(waits);
  }

  // ---- offscreen targets ------------------------------------------------------------------------------------------
  private target(name: string, w: number, h: number, opts: { depth?: boolean; msaa?: boolean }): Target {
    const old = this.targets.get(name);
    if (old && old.w === w && old.h === h) return old;
    if (old) old.garbage();
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    const extra: (() => void)[] = [];
    let draw = fbo, ms = false;
    if (opts.msaa && this.samples > 1) {
      ms = true;
      draw = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, draw);
      const color = gl.createRenderbuffer()!; gl.bindRenderbuffer(gl.RENDERBUFFER, color);
      gl.renderbufferStorageMultisample(gl.RENDERBUFFER, this.samples, gl.RGBA8, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, color);
      const depth = gl.createRenderbuffer()!; gl.bindRenderbuffer(gl.RENDERBUFFER, depth);
      gl.renderbufferStorageMultisample(gl.RENDERBUFFER, this.samples, gl.DEPTH_COMPONENT24, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depth);
      extra.push(() => { gl.deleteRenderbuffer(color); gl.deleteRenderbuffer(depth); gl.deleteFramebuffer(draw); });
    } else if (opts.depth) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      const depth = gl.createRenderbuffer()!; gl.bindRenderbuffer(gl.RENDERBUFFER, depth);
      gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depth);
      extra.push(() => gl.deleteRenderbuffer(depth));
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const t: Target = { w, h, tex, fbo, draw, ms, garbage: () => { gl.deleteTexture(tex); gl.deleteFramebuffer(fbo); extra.forEach((f) => f()); } };
    this.targets.set(name, t);
    return t;
  }
  private bind(t: Target | null, W: number, H: number, forDraw = true): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, t ? (forDraw ? t.draw : t.fbo) : null);
    gl.viewport(0, 0, t ? t.w : W, t ? t.h : H);
  }
  private resolve(t: Target): void {
    if (!t.ms) return;
    const gl = this.gl;
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, t.draw); gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, t.fbo);
    gl.blitFramebuffer(0, 0, t.w, t.h, 0, 0, t.w, t.h, gl.COLOR_BUFFER_BIT, gl.NEAREST);
  }
  /** Draw a full-screen pass with the given program into the current framebuffer. */
  private pass(name: string, setup: (p: WebGLProgram, l: (n: string) => WebGLUniformLocation | null) => void, textures: WebGLTexture[] = []): void {
    const gl = this.gl, p = this.prog(name);
    gl.useProgram(p);
    gl.disable(gl.DEPTH_TEST); gl.disable(gl.BLEND); gl.depthMask(false);
    textures.forEach((t, i) => { gl.activeTexture(gl.TEXTURE0 + i); gl.bindTexture(gl.TEXTURE_2D, t); });
    setup(p, (n) => this.loc(p, n, name));
    gl.bindVertexArray(this.emptyVao); gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.depthMask(true);
  }
  private copyTo(src: WebGLTexture, dst: Target | null, W: number, H: number): void {
    this.bind(dst, W, H);
    this.pass("copy", (_p, l) => this.gl.uniform1i(l("uTex"), 0), [src]);
  }

  /** Render the project at `timeMs` into the canvas. Pure function of (project, time) apart from GPU caches. */
  renderFrame(project: Project, timeMs: number): void {
    this.drawFrame(project, timeMs);
    // Translucent cards leave alpha < 1 in the framebuffer; the page compositor (used by the exporter's screenshot) would then
    // blend the picture over white. Force the output opaque so preview, export and readback all show the same pixels.
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.disable(gl.BLEND);
    gl.colorMask(false, false, false, true); gl.clearColor(0, 0, 0, 1); gl.clear(gl.COLOR_BUFFER_BIT); gl.colorMask(true, true, true, true);
  }

  private drawFrame(project: Project, timeMs: number): void {
    const gl = this.gl, W = this.canvas.width, H = this.canvas.height;
    const frame = evaluate(project, timeMs);
    const usesTransition = frame.scenes.some((s) => s.transitionU !== null && s.scene.transition && s.scene.transition.kind !== "none");
    const post = !lookIsNeutral(frame.look);
    gl.disable(gl.BLEND); gl.depthMask(true); gl.clearColor(0, 0, 0, 1); gl.clearDepth(1);
    if (!usesTransition && !post) {                                   // fast path: straight to the canvas
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, W, H);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      for (const s of frame.scenes) this.renderScene(s, this.lw, this.lh);
    } else {
      const acc = this.target("acc", W, H, { msaa: true, depth: true });
      this.bind(acc, W, H); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      for (const s of frame.scenes) {
        const tr = s.scene.transition;
        if (s.transitionU === null || !tr || tr.kind === "none") { this.bind(acc, W, H); this.renderScene(s, this.lw, this.lh); continue; }
        const inc = this.target("inc", W, H, { msaa: true, depth: true });
        this.bind(inc, W, H); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        this.renderScene(s, this.lw, this.lh);
        this.resolve(acc); this.resolve(inc);
        const out = this.target("mix", W, H, {});
        this.bind(out, W, H, false);
        const u = s.transitionU, e = ease("ease_in_out", u);
        this.pass("transition", (_p, l) => {
          gl.uniform1i(l("uA"), 0); gl.uniform1i(l("uB"), 1); gl.uniform1f(l("uE"), e); gl.uniform1f(l("uU"), u);
          gl.uniform1i(l("uKind"), TRANSITION_INDEX[tr.kind]); gl.uniform1f(l("uAspect"), W / H);
        }, [acc.tex, inc.tex]);
        this.copyTo(out.tex, acc, W, H);                              // the blended frame becomes the new accumulator
      }
      this.resolve(acc);
      this.postProcess(acc, frame.look, frame.frame, W, H);
    }
    if (frame.caption) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, W, H);
      this.renderCaption(frame.caption, this.lw, this.lh);
    }
  }

  /** Bloom (bright pass + separable gaussian at 1/2, 1/4, 1/8 resolution) and the final grade, written to the canvas. */
  private postProcess(src: Target, look: Look, frameNo: number, W: number, H: number): void {
    const gl = this.gl;
    const levels = look.bloom > 0 ? bloomLevels(look.bloom_radius) : 0;
    let b0 = src.tex, b1 = src.tex, b2 = src.tex;
    if (levels > 0) {
      const sizes = [2, 4, 8].map((d) => [Math.max(2, Math.round(W / d)), Math.max(2, Math.round(H / d))] as const);
      const blurAt = (i: number, from: WebGLTexture, thr: boolean): WebGLTexture => {
        const [w, h] = sizes[i]!, a = this.target(`ba${i}`, w, h, {}), b = this.target(`bb${i}`, w, h, {});
        const r = Math.max(0.5, look.bloom_radius);
        this.bind(a, W, H);
        if (thr) this.pass("bright", (_p, l) => { gl.uniform1i(l("uTex"), 0); gl.uniform1f(l("uThreshold"), look.bloom_threshold); }, [from]);
        else this.pass("copy", (_p, l) => gl.uniform1i(l("uTex"), 0), [from]);
        this.bind(b, W, H);
        this.pass("blur", (_p, l) => { gl.uniform1i(l("uTex"), 0); gl.uniform2f(l("uStep"), r / w, 0); }, [a.tex]);
        this.bind(a, W, H);
        this.pass("blur", (_p, l) => { gl.uniform1i(l("uTex"), 0); gl.uniform2f(l("uStep"), 0, r / h); }, [b.tex]);
        return a.tex;
      };
      b0 = blurAt(0, src.tex, true);
      b1 = blurAt(1, b0, false);
      b2 = levels > 2 ? blurAt(2, b1, false) : b1;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, W, H);
    this.pass("post", (_p, l) => {
      gl.uniform1i(l("uBase"), 0); gl.uniform1i(l("uB0"), 1); gl.uniform1i(l("uB1"), 2); gl.uniform1i(l("uB2"), 3);
      gl.uniform1f(l("uBloom"), levels > 0 ? look.bloom : 0); gl.uniform1f(l("uVig"), look.vignette); gl.uniform1f(l("uGrain"), look.grain);
      gl.uniform1f(l("uAber"), look.aberration); gl.uniform1f(l("uExp"), look.exposure); gl.uniform1f(l("uCon"), look.contrast);
      gl.uniform1f(l("uSat"), look.saturation); gl.uniform1f(l("uSeed"), frameNo); gl.uniform1f(l("uAspect"), W / H);
      gl.uniform1i(l("uLevels"), levels);
    }, [src.tex, b0, b1, b2]);
  }

  /** Subtitles are a flat overlay in canvas space (no scene camera, no depth), drawn last so nothing can cover them. */
  private renderCaption(c: CaptionState, W: number, H: number): void {
    const gl = this.gl, st = c.style;
    const key = `c|${c.text}|${st.font_family}|${st.font_size}|${st.font_weight}|${st.fill}|${st.background}|${st.background_opacity}|${st.max_width}|${W}`;
    const card = this.cacheCard(key, () => rasterCaption(c.text, st, W));
    const { view, proj } = cameraMatrices(W, H, DEFAULT_CAMERA);
    const cy = H * (1 - st.bottom_margin) - card.height / 2;           // canvas px from the top
    const m = modelMatrix(0, H / 2 - cy, 0, 0, 0, 0, card.width, card.height, 1);
    gl.disable(gl.DEPTH_TEST); gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); gl.depthMask(false);
    this.drawCard(card, m, multiply(proj, view), 1, [0, 0, 1, 1], 0, -1);
    gl.depthMask(true); gl.disable(gl.BLEND); gl.bindVertexArray(null);
  }

  private renderScene(s: SceneState, W: number, H: number): void {
    const gl = this.gl;
    // background (solid or vertical gradient), always behind everything
    gl.disable(gl.DEPTH_TEST); gl.disable(gl.BLEND); gl.depthMask(false);
    const bgp = this.prog("bg");
    gl.useProgram(bgp);
    const top = hexToRgb(s.scene.background), bottom = hexToRgb(s.scene.background_gradient ?? s.scene.background);
    gl.uniform3fv(this.loc(bgp, "uTop", "bg"), top); gl.uniform3fv(this.loc(bgp, "uBottom", "bg"), bottom);
    gl.bindVertexArray(this.emptyVao); gl.drawArrays(gl.TRIANGLES, 0, 3);
    const bd = s.scene.backdrop;
    if (bd && bd.kind !== "none") this.drawBackdrop(bd, s.localMs / 1000, W, H);
    gl.clear(gl.DEPTH_BUFFER_BIT); gl.depthMask(true); gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL);

    const cam: CameraState = s.camera;
    const { view, proj, eye } = cameraMatrices(W, H, cam);
    const vp = multiply(proj, view);
    this.vpCache = vp;
    const L = s.scene.lighting;
    const yaw = rad(L.key_yaw), pitch = rad(L.key_pitch);
    const lightDir: Vec3 = [Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), Math.cos(yaw) * Math.cos(pitch)];
    const keyCol = hexToLinear(L.key_color).map((c) => c * L.key_intensity);

    const opaque: Draw[] = [], transparent: Draw[] = [];
    for (const o of s.objects) {
      const t = o.t, p: Vec3 = [t.x - W / 2, H / 2 - t.y, t.z];
      const viewZ = transformPoint(view, p)[2];            // more negative = farther
      const el = o.element;
      if (el.type === "mesh") {
        const gm = this.meshFor(el);
        const m = modelMatrix(p[0], p[1], p[2], t.rotation_x, t.rotation_y, t.rotation, t.scale_x, t.scale_y, t.scale_z);
        const run = () => this.drawMesh(gm, el, m, vp, eye, lightDir, keyCol, L, t.opacity);
        (t.opacity < 0.999 ? transparent : opaque).push({ depth: viewZ, run });
      } else if (el.type === "particles") {
        transparent.push({ depth: viewZ, run: () => this.drawParticles(el, o, p, view, vp, s.localMs / 1000) });
      } else {
        transparent.push({ depth: viewZ, layer: el.layer ?? 0, run: () => this.drawFlat(el, o, p, vp) });
      }
    }
    for (const d of opaque) d.run();
    gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); gl.depthMask(false);
    transparent.sort((a, b) => ((a.layer ?? 0) - (b.layer ?? 0)) || (a.depth - b.depth));   // layer first, then far (most negative z) to near
    for (const d of transparent) d.run();
    gl.depthMask(true); gl.disable(gl.BLEND); gl.bindVertexArray(null);
  }

  private drawBackdrop(bd: NonNullable<SceneState["scene"]["backdrop"]>, timeS: number, W: number, H: number): void {
    const gl = this.gl;
    // The smooth backdrops (aurora, glow, clouds) are by far the most expensive pass (several noise octaves per pixel), and they have no
    // detail finer than a few dozen pixels: they are computed at 1/4 size and upsampled. Grid and dots need crisp lines, so they stay full size.
    const smooth = bd.kind === "aurora" || bd.kind === "radial" || bd.kind === "noise";
    let back: { fb: WebGLFramebuffer | null; vp: Int32Array } | null = null;
    if (smooth) {
      back = { fb: gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null, vp: gl.getParameter(gl.VIEWPORT) as Int32Array };
      const lo = this.target("bdlow", Math.max(2, Math.round(back.vp[2]! / 4)), Math.max(2, Math.round(back.vp[3]! / 4)), {});
      this.bind(lo, W, H); gl.disable(gl.BLEND); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    } else { gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); }
    const p = this.prog("backdrop"); gl.useProgram(p);
    const l = (n: string) => this.loc(p, n, "backdrop");
    gl.uniform1i(l("uKind"), BACKDROP_INDEX[bd.kind]); gl.uniform1f(l("uTime"), timeS); gl.uniform1f(l("uAspect"), W / H);
    gl.uniform3fv(l("uA"), hexToRgb(bd.color_a)); gl.uniform3fv(l("uB"), hexToRgb(bd.color_b)); gl.uniform3fv(l("uC"), hexToRgb(bd.color_c));
    gl.uniform1f(l("uIntensity"), bd.intensity); gl.uniform1f(l("uSpeed"), bd.speed); gl.uniform1f(l("uScale"), bd.scale); gl.uniform2f(l("uRes"), W, H);
    gl.bindVertexArray(this.emptyVao); gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (back) {
      const lo = this.targets.get("bdlow")!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, back.fb); gl.viewport(back.vp[0]!, back.vp[1]!, back.vp[2]!, back.vp[3]!);
      const cp = this.prog("copy"); gl.useProgram(cp);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, lo.tex); gl.uniform1i(this.loc(cp, "uTex", "copy"), 0);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    gl.disable(gl.BLEND);
  }

  private drawParticles(el: ParticlesElement, o: ObjectState, p: Vec3, view: Mat4, vp: Mat4, timeS: number): void {
    const gl = this.gl, prog = this.prog("particles"), t = o.t;
    // `reveal` scales how many particles exist (0 = none, 1 = all) so an emitter can be faded in by the timeline.
    const count = Math.min(MAX_PARTICLES, Math.round(el.count * Math.min(Math.max(t.reveal ?? 1, 0), 1)));
    if (count <= 0 || t.opacity <= 0) return;
    gl.useProgram(prog);
    const l = (n: string) => this.loc(prog, n, "particles");
    gl.uniformMatrix4fv(l("uViewProj"), false, vp);
    gl.uniform3f(l("uCenter"), p[0], p[1], p[2]);
    gl.uniform3f(l("uRight"), view[0]!, view[4]!, view[8]!); gl.uniform3f(l("uUp"), view[1]!, view[5]!, view[9]!);
    gl.uniform1f(l("uTime"), timeS); gl.uniform2f(l("uArea"), el.area_w, el.area_h); gl.uniform1i(l("uKind"), PARTICLE_INDEX[el.kind]);
    gl.uniform1ui(l("uSeed"), el.seed >>> 0);
    gl.uniform1f(l("uSize"), el.size); gl.uniform1f(l("uSizeVar"), el.size_variation); gl.uniform1f(l("uSpeed"), el.speed);
    gl.uniform1f(l("uGravity"), el.gravity); gl.uniform1f(l("uDrift"), el.drift); gl.uniform1f(l("uLife"), Math.max(0.05, el.lifetime_ms / 1000));
    gl.uniform1f(l("uOpacity"), t.opacity); gl.uniform1f(l("uScale"), t.scale_x);
    gl.uniform3fv(l("uColA"), hexToRgb(el.color)); gl.uniform3fv(l("uColB"), hexToRgb(el.color_to ?? el.color)); gl.uniform1f(l("uGlow"), el.glow);
    if (el.additive) gl.blendFunc(gl.ONE, gl.ONE); else gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(this.emptyVao);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  /** Text and shapes: an optional shadow/glow layer under the glyph layer, with the entrance (reveal) applied per piece. */
  private drawFlat(el: TextElement | ShapeElement | ImageElement | VideoElement | HtmlElement, o: ObjectState, p: Vec3, vp: Mat4): void {
    if (el.type === "video" && !this.playing) { const e = this.videoFor(o.id, el.asset_id); if (e?.ok && o.mediaTimeMs !== undefined && Math.abs(e.video.currentTime * 1000 - o.mediaTimeMs) > 50) e.video.currentTime = Math.max(0, o.mediaTimeMs / 1000); }
    const t = o.t, reveal = t.reveal ?? 1, blur = t.blur ?? 0;
    const draw = (t as unknown as Record<string, number>).draw ?? 1, morphU = (t as unknown as Record<string, number>).morph ?? 0;
    const glyph = this.cardFor(el, "glyph", draw, morphU, o.mediaTimeMs ?? 0), shadow = el.type !== "html" && hasShadow(el) ? this.cardFor(el, "shadow", draw, morphU) : null;
    const E = modelMatrix(p[0], p[1], p[2], t.rotation_x, t.rotation_y, t.rotation, t.scale_x, t.scale_y, 1);
    const effect = el.reveal_effect ?? (el.type === "shape" || el.type === "image" ? "scale" : "fade"), dist = el.reveal_distance ?? 40;
    const stagger = el.type === "text" ? el.reveal_stagger ?? 0.5 : 0;
    const split = el.type === "text" ? el.split ?? "none" : "none";
    const units = split !== "none" && glyph.units ? glyph.units[split] : null;
    const whole: Unit = { x: 0, y: 0, w: glyph.width, h: glyph.height };
    const pieces = units && units.length > 1 ? units : [whole];
    const n = pieces.length;
    const done = reveal >= 1 && blur <= 0;

    if (shadow) {                                                     // the glow follows the average progress of the pieces
      let sum = 0;
      for (let i = 0; i < n; i++) sum += revealFx(effect, pieceProgress(reveal, i, n, stagger), dist).alpha;
      const fx = revealFx(effect, n === 1 ? reveal : 1, dist);
      const mean = sum / n;
      this.drawPiece(shadow, E, whole, { ...fx, alpha: n === 1 ? mean : mean * mean * mean }, t.opacity, blur);
    }
    if (done) { this.drawPiece(glyph, E, whole, revealFx(effect, 1, dist), t.opacity, 0); return; }
    for (let i = 0; i < n; i++) {
      const fx = revealFx(effect, pieceProgress(reveal, i, n, stagger), dist);
      if (fx.alpha <= 0.001) continue;
      this.drawPiece(glyph, E, pieces[i]!, fx, t.opacity, blur);
    }
  }

  private drawPiece(card: GpuCard, E: Mat4, u: Unit, fx: ReturnType<typeof revealFx>, opacity: number, extraBlur: number): void {
    const whole = u.w === card.width && u.h === card.height;
    const blur = Math.max(fx.blur, extraBlur);
    // pieces are inflated a little so strokes and soft edges are not clipped; blurred pieces need more room
    const inf = whole ? 0 : Math.min(8, 2 + blur * 0.5);
    const x0 = Math.max(0, u.x - inf), y0 = Math.max(0, u.y - Math.min(card.height * 0.25, inf + 6));
    const x1 = Math.min(card.width, u.x + u.w + inf), y1 = Math.min(card.height, u.y + u.h + Math.min(card.height * 0.25, inf + 6));
    const w = x1 - x0, h = y1 - y0;
    const cx = x0 + w / 2 - card.width / 2, cy = card.height / 2 - (y0 + h / 2);
    let M = multiply(E, translation(cx, cy + fx.dy, 0));
    if (fx.scale !== 1) M = multiply(M, scaling(fx.scale, fx.scale, 1));
    if (fx.rotX !== 0) M = multiply(M, rotationX(rad(-fx.rotX)));
    M = multiply(M, scaling(w, h, 1));
    this.drawCard(card, M, this.vpCache!, opacity * fx.alpha, [x0 / card.width, y0 / card.height, w / card.width, h / card.height],
                  blur > 0 ? Math.log2(1 + blur * 2) : 0, fx.wipe);
  }
  private vpCache: Mat4 | null = null;

  private drawMesh(gm: GpuMesh, el: MeshElement, model: Mat4, vp: Mat4, eye: Vec3, lightDir: Vec3, keyCol: number[],
                   L: { ambient: number; sky: string; ground: string }, opacity: number): void {
    const gl = this.gl, p = this.prog("mesh"), l = (n: string) => this.loc(p, n, "mesh");
    gl.useProgram(p);
    gl.uniformMatrix4fv(l("uModel"), false, model); gl.uniformMatrix4fv(l("uViewProj"), false, vp);
    gl.uniform3fv(l("uAlbedo"), hexToLinear(el.color)); gl.uniform1f(l("uMetal"), el.metalness); gl.uniform1f(l("uRough"), el.roughness);
    gl.uniform3fv(l("uEmissive"), hexToLinear(el.emissive).map((c) => c * el.emissive_intensity)); gl.uniform1f(l("uOpacity"), opacity);
    gl.uniform3fv(l("uEye"), eye); gl.uniform3fv(l("uLightDir"), lightDir); gl.uniform3fv(l("uKey"), keyCol);
    gl.uniform1f(l("uAmbient"), L.ambient); gl.uniform3fv(l("uSky"), hexToLinear(L.sky)); gl.uniform3fv(l("uGround"), hexToLinear(L.ground));
    gl.bindVertexArray(gm.vao); gl.drawElements(gl.TRIANGLES, gm.count, gl.UNSIGNED_INT, 0);
  }

  private drawCard(card: GpuCard, model: Mat4, vp: Mat4, opacity: number, rect: [number, number, number, number], lod: number, wipe: number): void {
    const gl = this.gl, p = this.prog("card"), l = (n: string) => this.loc(p, n, "card");
    gl.useProgram(p);
    gl.uniformMatrix4fv(l("uModel"), false, model); gl.uniformMatrix4fv(l("uViewProj"), false, vp);
    gl.uniform1f(l("uOpacity"), opacity); gl.uniform1i(l("uTex"), 0); gl.uniform4f(l("uRect"), rect[0], rect[1], rect[2], rect[3]);
    gl.uniform1f(l("uLod"), lod); gl.uniform1f(l("uWipe"), wipe);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, card.tex);
    gl.bindVertexArray(this.quadMesh.vao); gl.drawElements(gl.TRIANGLES, this.quadMesh.count, gl.UNSIGNED_INT, 0);
  }

  toPNGDataURL(): string { return this.canvas.toDataURL("image/png"); }
  toJPEGDataURL(quality = 0.93): string { return this.canvas.toDataURL("image/jpeg", quality); }
}

export type { ObjectState };
